// OLCI
import * as _ from 'lodash';
import * as utils from '../util';
import { constants } from '../constants';
import { GuestActionTypes } from '../actions';
import { Guest, GuestBooking, GuestsSecurityPhoto, PhotoListResponse } from '../models';
import { ModuleSpecificValue } from '../models/guest';

export interface State {
  loading: boolean;
  loadingPhoto: boolean;
  progressLoaded: boolean;
  guests: Guest[];
  selectedGuestId: number | null;
  selectedGuest: Guest | any;
  selectedOtherGuest: GuestBooking | any;
  error: any;
}

export const initialState: State = {
  loading: false,
  loadingPhoto: false,
  progressLoaded: true,
  guests: [],
  selectedGuestId: null,
  selectedGuest: null,
  selectedOtherGuest: null,
  error: null,
};

export function reducer(state: State = initialState , action: any): State {
  switch (action.type) {
    case GuestActionTypes.LOAD_GUESTS_PROFILE_BOOKING_MICROSERVICE: {
      return loadGuestProfileBookingsReducer(state,action.payload);
    }
    case GuestActionTypes.UPDATE_GUESTS_FNAME: {
      return updateGuestsFirstNameReducer(state,action.payload);
    }
    case GuestActionTypes.SELECT_GUESTID_BOOKINGID: {
      const selectedOtherGuest = {
        guestId: action.payload.guestId,
        bookingId: action.payload.bookingId,
      };
      return {
        ...state,
        selectedOtherGuest,
      };
    }
    case GuestActionTypes.RESET_SELECTED_OTHER_GUEST: {
      return {
        ...state,
        selectedOtherGuest: null,
      };
    }
    case GuestActionTypes.RESET_GUESTS_SELECTED_FOR_CHECKIN: {
      const resetedGuests = state.guests.map(guest => ({
        ...guest,
        selectedForCheckin: false,
      }));
      return {
        ...state,
        guests: resetedGuests,
      };
    }
    case GuestActionTypes.SELECT_GUEST_FOR_EDIT: {
      const resetedGuests = state.guests.map(guest => ({
        ...guest,
        selectedForCheckin: action.payload === guest.guestId,
      }));
      return {
        ...state,
        guests: resetedGuests,
      };
    }
    case GuestActionTypes.UPDATE_GUESTS_TO_CHECKIN_VM: {
      return updateGuestsToCheckInVMReducer(state,action.payload);
    }
    case GuestActionTypes.SAVE_GUEST_PHOTO:
    case GuestActionTypes.UPDATE_GUEST_PHOTO: {
      return {
        ...state,
        loadingPhoto: true,
      };
    }
    case GuestActionTypes.UPDATE_GUEST_PHOTO_SUCCESS:
    case GuestActionTypes.SAVE_GUEST_PHOTO_SUCCESS: {
      return updateSaveGuestPhotoSuccessReducer(state, action.payload);
    }
    case GuestActionTypes.RETRIEVE_GUEST_SECURITY_PHOTO_LIST_SUCCESS: {
      return retrieveGuestSecurityPhotoListSuccessReducer(state, action.payload)
    }
    case GuestActionTypes.UPDATE_PROGRESS_LOADED:
    case GuestActionTypes.GET_PERCENTAGE_COMPLETION: {
      return {
        ...state,
        error: null,
        progressLoaded: false,
      };
    }
    case GuestActionTypes.UPDATE_COMPLETION_PERCENTAGE_GUEST: {
      return updateCompletionPercentageGuestReducer(state, action.payload);
    }
    case GuestActionTypes.LOAD_PDETAIL:
    case GuestActionTypes.UPDATE_SELECTED_GUEST: {
      return {
        ...state,
        error: null,
        selectedGuestId: action.payload,
        selectedGuest: retrieveSelectedGuest(action.payload, state),
      };
    }
    case GuestActionTypes.CLEAR_SELECTED_GUEST: {
      return {
        ...state,
        selectedGuestId: null,
        selectedGuest: null,
      };
    }
    case GuestActionTypes.CLEAR_GUESTS_LIST: {
      return {
        ...state,
        guests: [],
      };
    }
    case GuestActionTypes.UPDATE_GUESTS_INFO: {
      return updateGuestsInfoReducer(state, action.payload);
    }
    default:
      return state;
  }
}

function retrieveSelectedGuest(selectedGuestId: string | number, state: State): Guest | null {
  const selectedGuest = state.guests.filter((guest: Guest) => guest.guestId === selectedGuestId);
  return selectedGuest.length > 0 ? selectedGuest[0] : null;
}

function loadGuestProfileBookingsReducer(state: State,response: any) : State {
  if (response.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
    const actionError =
      response.errors && response.errors.length ? response.errors[0] : {};
    return {
      ...state,
      loading: false,
      error: actionError,
    };
  } else {
    return {
      ...state,
      loading: false,
      error: null,
      guests: [...response.payload],
    };
  }
}

function updateGuestsFirstNameReducer(state: State, actionPayload: any) : State {
  const guests = state.guests.map((guest) => {
    if (guest.guestId === actionPayload.guestId) {
      return {
        ...guest,
        middleName: utils.capitalizeString(actionPayload.middleName),
        suffix: actionPayload.suffix,
        minor: actionPayload.isMinor,
      }
    }
    return guest;
  });
  return {
    ...state,
    guests,
  };
}

function updateGuestsToCheckInVMReducer(state: State, actionPayload: any): State {
  return {
    ...state,
    loading: false,
    guests: state.guests.map(item =>
      item.guestId === actionPayload
        ? { ...item, selectedForCheckin: !item.selectedForCheckin }
        : item
    ),
  };
}

function updateSaveGuestPhotoSuccessReducer(state: State, actionPayload: any): State {
  if (actionPayload.status !== constants.MICROSERVICE_RESPONSE_STATUS.SUCCESSFUL_REQUEST) {
    return {
      ...state,
      loadingPhoto: false,
      loading: false,
      error: actionPayload.statusCode,
    };
  }

  return {
    ...state,
    loadingPhoto: false,
    guests: state.guests.map(guest =>
      guest.guestId === actionPayload.guestID
        ? {
            ...guest,
            base64Image: actionPayload.imageData,
            photoId: actionPayload.guestPhotoID || actionPayload.guestPhotoId,
          }
        : guest
    ),
  };
}

function retrieveGuestSecurityPhotoListSuccessReducer(state: State, actionPayload: PhotoListResponse): State {
  if (actionPayload.status !== constants.MICROSERVICE_RESPONSE_STATUS.SUCCESSFUL_REQUEST) {
    return photoResponseIsNotValid(state, actionPayload.status);
  }
  return photoResponseIsValid(state, actionPayload.payload.guestsSecurityPhoto);
}

function photoResponseIsNotValid(state: State, status: any) {
  return {
    ...state,
    loadingPhoto: false,
    loading: false,
    error: status,
  };
}

function photoResponseIsValid(state: State, guestsSecurityPhoto: GuestsSecurityPhoto[]) {
  return {
    ...state,
    loadingPhoto: false,
    guests: state.guests.map(guest => {
      const guestPhoto = guestsSecurityPhoto.find(
        gp => gp.guestId.toString() === guest.guestId.toString()
      );
  
      return guestPhoto
        ? {
            ...guest,
            base64Image: guestPhoto.base64ImageData,
          }
        : guest;
    }),
  };
}

function updateCompletionPercentageGuestReducer(state: State, actionPayload: any): State {
  let guestCheckinStatuses: any;
      if (actionPayload.payload && actionPayload.payload.checkinStatuses) {
        guestCheckinStatuses = actionPayload.payload.checkinStatuses;
      }

      if (actionPayload.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
        return {
          ...state,
          loading: false,
          progressLoaded: true,
          error: actionPayload.error,
        };
      }

      const guestsList = state.guests.map((guest) => {
        const guestId = guest.guestId.toString();
        if (guestCheckinStatuses.length) {
          const guestCheckinStatus = guestCheckinStatuses.find(
            (g: any) => g.guestId === guestId
          );
          return {
            ...guest,
            boardingPassPendingStatus: false,
            moduleSpecificValue: guestCheckinStatus.moduleSpecificValue,
            olciStatus: guestCheckinStatus.onlineCheckinStatus,
          };
        }
        return guest;
      });

      return {
        ...state,
        guests: guestsList,
        progressLoaded: true,
      };
}

function updateGuestsInfoReducer(state: State, response: any): State {
  if (response.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
    const actionError = response.errors.length ? response.errors[0] : {};
    return {
      ...state,
      loading: false,
      error: actionError,
    };
  } else {
    let newGuestsList: any = [];
    const currentGuestsList = [...state.guests];
    const bookings = _.get(response, 'payload.bookings', []);
    if (bookings.length && currentGuestsList.length) {
      newGuestsList = currentGuestsList.map((c) => {
        const updatedGuest = { ...c };
        bookings.forEach((b: any) => {
          const guest = b.guests.find((g: any) => g.guestId === c.guestId);
          updateGuest(guest, updatedGuest,b);
          return updatedGuest;
        });
        return updatedGuest;
      });
    }

    return {
      ...state,
      loading: false,
      error: null,
      guests: [...newGuestsList],
    };
  }
}

function updateGuest(guest: any, updatedGuest: any, booking: any) {
  if (guest) {
    updatedGuest.middleName = utils.capitalizeNameAllWords(
      guest.middleName
    );
    updatedGuest.suffix = guest.suffix;
    updatedGuest.moduleSpecificValue = mapApiResponse(guest);
    updatedGuest.selectedLanguageCode = guest.selectedLanguages;
    updatedGuest.photoUri = _.get(
      guest,
      'travelPhoto.photoUri',
      ''
    );
    updatedGuest.isLocked = booking.isLocked;
    updatedGuest.countryCode = booking.countryCode;
  }
}

function mapApiResponse(guestBeginDetails: any): ModuleSpecificValue {
  return {
    addressContact: guestBeginDetails.isAddressComplete,
    arrivalTimes: guestBeginDetails.isAppointmentComplete,
    contactInfo: guestBeginDetails.isContactInfoComplete,
    cruiseContract: guestBeginDetails.isReservationContractComplete,
    emergencyContact: guestBeginDetails.isEmergencyContactComplete,
    expenseAccount: guestBeginDetails.isExpenseComplete,
    expressPassSmartShips: guestBeginDetails.isBoardingPassComplete,
    healthRiskAcknowledgement:
      guestBeginDetails.isHealthRiskAcknowledgementComplete,
    immigrationInfo: guestBeginDetails.isTravelDocumentsComplete,
    personalDetails: guestBeginDetails.isPersonalDetailComplete,
  };
}

export const getGuests = (state: State) =>
  state && state.guests ? state.guests : null;
export const getSelectedOtherGuest = (state: State) => state.selectedOtherGuest;
export const getSelectedGuestId = (state: State) => state.selectedGuestId;
export const getSelectedGuest = (state: State) => state.selectedGuest;
export const getProgressIsLoaded = (state: State) => state.progressLoaded;
export const loadingPhoto = (state: State) => state.loadingPhoto;
export const getError = (state: State) => state.error;
